import { ServerConnection } from "@/context/server";
import { machineRouteManager, machineDefaultRouteUrl, type MachineRouteKind, type MachineRouteInput } from "@/fleet/machine-route-manager";
import type { MachineAccessGrant, PublicMachineRoute } from "@/transport/fleet";

export type FleetMachine = {
  name: string;
  display_name?: string;
  public_url: string;
  public_routes?: PublicMachineRoute[];
  /** The key public routes seal requests to (backend/e2e). */
  e2e_public_key?: string;
  route_policy?: MachineRouteInput["route_policy"];
  local_url?: string;
  provider?: string;
  auth?: string;
  accessToken?: string;
  accessTokenExpiresAt?: number;
  accessError?: string;
  updated_at: number;
  last_seen_at?: number;
  runtime_version?: string;
  supervisor_version?: string;
  available_release?: string;
  desired_release?: string;
  update_state?: string;
  update_error?: string;
  update_attempted_at?: number;
  update_generation?: number;
  health?: FleetMachineHealth;
  metrics?: FleetMachineMetrics;
  direct?: boolean;
};

export type FleetMachineMetrics = {
  name: string;
  cpu_percent: number;
  memory_percent: number;
  memory_used_bytes: number;
  memory_total_bytes: number;
  load_1: number;
  cpu_count: number;
  collected_at: number;
};

export type FleetHeartbeatUpdate = {
  name: string;
  last_seen_at: number;
  health: FleetMachineHealth;
  runtime_version?: string;
  supervisor_version?: string;
};

export type FleetUpdate = {
  name: string;
  desired_release?: string;
  update_state?: string;
  update_error?: string;
  update_attempted_at?: number;
  update_generation?: number;
};

export type FleetEnrollmentProgress = {
  phase: "installing" | "registered";
  name?: string;
  updated_at: number;
};

export type FleetMachineHealth = {
  state: "ready" | "offline" | "auth" | "unknown" | "degraded";
  label: string;
  message: string;
  checked_at?: number;
  last_ready_at?: number;
};

export type FleetMachineView = FleetMachine & {
  key: ServerConnection.Key;
  health?: FleetMachineHealth;
};

export type { MachineRouteKind };

export const MACHINE_HEARTBEAT_STALE_SECONDS = 120;

export function enrollmentProgressFromFleetEvent(
  type: string | undefined,
  properties: unknown,
): FleetEnrollmentProgress | undefined {
  if (
    type !== "fleet.enrollment.started" &&
    type !== "fleet.machine.registered"
  )
    return undefined;
  if (!properties || typeof properties !== "object") return undefined;
  const value = properties as { name?: unknown; updated_at?: unknown };
  if (typeof value.updated_at !== "number") return undefined;
  return {
    phase: type === "fleet.enrollment.started" ? "installing" : "registered",
    name: typeof value.name === "string" && value.name ? value.name : undefined,
    updated_at: value.updated_at,
  };
}

export const machineKey = (url: string) =>
  ServerConnection.Key.make(url.replace(/\/+$/, ""));

export function machineBaseUrl(
  machine: MachineRouteInput,
) {
  return machineRouteManager.destination(machine);
}

export const machineUrl = (
  machine: MachineRouteInput,
  path: string,
) => `${machineBaseUrl(machine)}${path}`;

export function machineRouteLabel(
  machine: MachineRouteInput & Pick<FleetMachine, "direct">,
) {
  if (machine.direct) return "Direct local connection";
  const route = machineRouteManager.observed(machine);
  if (route?.kind === "direct-local") return "Direct local connection";
  if (route?.kind === "unavailable") return machineRouteManager.localOnlyURL(machine)
    ? "Connection unavailable · Local runtime unreachable"
    : "Connection unavailable · Secure tunnel unreachable";
  return `Direct · ${route?.url || machineDefaultRouteUrl(machine) || "Cloudflare route pending"}`;
}

export function effectiveMachineHealth(
  machine: Pick<FleetMachine, "direct" | "last_seen_at" | "health">,
  nowSeconds = Date.now() / 1000,
  live = false,
  staleSeconds = MACHINE_HEARTBEAT_STALE_SECONDS,
): FleetMachineHealth | undefined {
  if (machine.direct) return completeMachineHealth(machine.health);
  if (live) {
    // The machine's own event stream proves it is reachable. Heartbeat age only
    // measures the control-plane path, so it cannot mark this machine offline.
    const reported = completeMachineHealth(machine.health);
    return reported && reported.state !== "offline"
      ? reported
      : { state: "ready", label: "Connected", message: "Live connection to this machine." };
  }
  const lastSeenAt = machine.last_seen_at ?? machine.health?.checked_at ?? 0;
  if (!lastSeenAt || nowSeconds - lastSeenAt > staleSeconds) {
    return completeMachineHealth({
      state: "offline",
      label: "",
      message: "",
      checked_at: lastSeenAt || undefined,
      last_ready_at: machine.health?.last_ready_at,
    });
  }
  return completeMachineHealth(machine.health);
}

const HEALTH_DEFAULTS: Record<FleetMachineHealth["state"], Pick<FleetMachineHealth, "label" | "message">> = {
  ready: { label: "Ready", message: "Machine heartbeat is healthy." },
  degraded: { label: "Degraded", message: "Machine heartbeat reported an unhealthy backend." },
  offline: { label: "Offline", message: "Machine heartbeat is stale or unavailable." },
  auth: { label: "Access unavailable", message: "Could not issue temporary machine credentials." },
  unknown: { label: "Checking", message: "Waiting for machine health." },
};

export function completeMachineHealth(
  health?: FleetMachineHealth,
): FleetMachineHealth | undefined {
  if (!health) return undefined;
  const fallback = HEALTH_DEFAULTS[health.state];
  return {
    ...health,
    label: health.label || fallback.label,
    message: health.message || fallback.message,
  };
}

export const machineReady = (health?: FleetMachineHealth) =>
  health?.state === "ready";
export const machineFunky = (health?: FleetMachineHealth) =>
  !!health && health.state !== "ready";

export function mergeFleetSnapshots(
  current: FleetMachine[],
  next: FleetMachine[],
) {
  const previous = new Map(current.map((machine) => [machine.name, machine]));
  return next.map((machine) => {
    const before = previous.get(machine.name);
    return {
      ...machine,
      // Fleet snapshots own inventory; the access controller owns current grants and failures.
      ...(before ? {
        accessToken: before.accessToken,
        accessTokenExpiresAt: before.accessTokenExpiresAt,
        accessError: before.accessError,
      } : {}),
      metrics: machine.metrics ?? before?.metrics,
    };
  });
}

/** Takes grants a fleet snapshot carried; one expiring no later than the grant held is ignored. */
export function withAccessGrants(machines: FleetMachine[], grants: ReadonlyMap<string, MachineAccessGrant>) {
  return machines.map((machine) => {
    const grant = grants.get(machine.name);
    if (!grant || (machine.accessTokenExpiresAt ?? 0) >= grant.expires_at) return machine;
    return { ...machine, auth: undefined, accessToken: grant.token, accessTokenExpiresAt: grant.expires_at, accessError: undefined };
  });
}

export function machineLastSeenAt(
  machine: Pick<FleetMachine, "updated_at" | "last_seen_at" | "metrics">,
) {
  return Math.max(
    machine.updated_at,
    machine.last_seen_at ?? 0,
    machine.metrics?.collected_at ?? 0,
  );
}

export async function retainFleetSnapshotOnFailure(
  request: Promise<FleetMachine[]>,
  snapshot: () => FleetMachine[],
): Promise<FleetMachine[]> {
  try {
    return await request;
  } catch (error) {
    if (!(error instanceof TypeError)) throw error;
    return snapshot();
  }
}

/** Replaces every machine's metrics with the latest snapshot; machines missing from it lose theirs. */
export function withMetricsSnapshot(machines: FleetMachine[], snapshots: FleetMachineMetrics[]) {
  const byName = new Map(snapshots.map((metrics) => [metrics.name, metrics]));
  return machines.map((machine) => {
    const metrics = byName.get(machine.name);
    return { ...machine, metrics, last_seen_at: metrics?.collected_at ?? machine.last_seen_at };
  });
}

/** Applies one metrics event unless the machine already has a newer sample. */
export function withMetrics(machines: FleetMachine[], metrics: FleetMachineMetrics) {
  return machines.map((machine) => {
    if (machine.name !== metrics.name || (machine.metrics && machine.metrics.collected_at >= metrics.collected_at))
      return machine;
    return { ...machine, metrics, last_seen_at: metrics.collected_at };
  });
}

/** Applies a heartbeat unless the machine was already seen at or after it. */
export function withHeartbeat(machines: FleetMachine[], heartbeat: FleetHeartbeatUpdate) {
  return machines.map((machine) => {
    if (machine.name !== heartbeat.name) return machine;
    if ((machine.last_seen_at ?? 0) >= heartbeat.last_seen_at) return machine;
    return {
      ...machine,
      last_seen_at: heartbeat.last_seen_at,
      health: completeMachineHealth(heartbeat.health),
      runtime_version: heartbeat.runtime_version ?? machine.runtime_version,
      supervisor_version: heartbeat.supervisor_version ?? machine.supervisor_version,
    };
  });
}

export function withUpdate(machines: FleetMachine[], update: FleetUpdate) {
  return machines.map((machine) => (machine.name === update.name ? { ...machine, ...update } : machine));
}

/**
 * Records a failed access grant. A grant that is still usable keeps working;
 * otherwise the machine reads as access-unavailable until a retry succeeds.
 */
export function withAccessFailure(machine: FleetMachine, message: string, usable: boolean): FleetMachine {
  return {
    ...machine,
    accessToken: usable ? machine.accessToken : undefined,
    accessTokenExpiresAt: usable ? machine.accessTokenExpiresAt : undefined,
    accessError: message,
    health: usable ? machine.health : {
      state: "auth",
      label: "Access unavailable",
      message: "Could not issue temporary machine credentials. Kettle will retry automatically.",
    },
  };
}
